// Hard-reset del Service Worker — desregistra TODOS los SW y purga TODAS las
// cachés del navegador. Lo dispara la acción manual "Recargar ahora" del toast
// de nueva versión (plugins/pwa-update.client.ts) para garantizar que UN SOLO
// click escape de un SW/precache viejo atorado (incidente recurrente Dra. Sonia:
// el toast volvía tras recargar porque el SW viejo seguía sirviendo el shell
// precacheado; un reload normal no lo desregistra — ver G-263).
//
// Extraído como función pura (deps inyectables) para testearlo sin browser, igual
// que utils/pwaVersion.ts. Best-effort por diseño: cada paso traga sus errores y
// NUNCA lanza — un fallo no debe atrapar al usuario impidiendo el reload posterior.
//
// INVARIANTE: NO toca `localStorage`. El token de sesión de Supabase vive ahí;
// borrarlo desloguearía al usuario. Sólo SW + Cache Storage (donde vive el shell
// precacheado viejo). Tras el reload el SW ya no intercepta → fetch fresco del
// edge (sw.js/version.json se sirven `no-store`, G-263) → bundle nuevo.

export interface SwResetDeps {
  /** navigator.serviceWorker.getRegistrations() — o [] si no hay SW. */
  getRegistrations: () => Promise<ReadonlyArray<{ unregister: () => Promise<boolean> }>>
  /** caches.keys() — nombres de todas las cachés. */
  cacheKeys: () => Promise<string[]>
  /** caches.delete(key). */
  cacheDelete: (key: string) => Promise<boolean>
}

export interface SwResetResult {
  unregistered: number
  cachesDeleted: number
}

export async function purgeServiceWorkerAndCaches(deps: SwResetDeps): Promise<SwResetResult> {
  let unregistered = 0
  let cachesDeleted = 0

  try {
    const regs = await deps.getRegistrations()
    const results = await Promise.all(regs.map(r => r.unregister().catch(() => false)))
    unregistered = results.filter(Boolean).length
  } catch { /* best-effort: sin SW o API bloqueada → seguimos con las cachés */ }

  try {
    const keys = await deps.cacheKeys()
    const results = await Promise.all(keys.map(k => deps.cacheDelete(k).catch(() => false)))
    cachesDeleted = results.filter(Boolean).length
  } catch { /* best-effort: Cache Storage no disponible → devolvemos lo que sí logramos */ }

  return { unregistered, cachesDeleted }
}

/** Tope del purgado antes de recargar igual. Si la limpieza se cuelga, recargar
 *  tarde es mejor que dejar al usuario mirando una pantalla muerta. */
export const SW_RESET_TIMEOUT_MS = 1500

/**
 * Las deps reales del browser, en un solo lugar.
 *
 * Vive aquí y no en cada call site porque son DOS los que necesitan el hard reset
 * y tienen que comportarse idéntico: el toast de nueva versión
 * (`plugins/pwa-update.client.ts`) y el boundary de error (`error.vue`). Cuando
 * solo el toast lo hacía, el botón "Recargar" de `error.vue` era un
 * `window.location.reload()` pelón — que NO desregistra el SW, así que el SW viejo
 * volvía a servir el shell cacheado, volvía a pedir el chunk borrado y volvía a
 * mostrar el mismo error: un bucle del que el usuario no podía salir (G-272).
 */
export function browserSwResetDeps (): SwResetDeps {
  return {
    getRegistrations: () => ('serviceWorker' in navigator)
      ? navigator.serviceWorker.getRegistrations()
      : Promise.resolve([]),
    cacheKeys: () => (typeof caches !== 'undefined') ? caches.keys() : Promise.resolve([]),
    cacheDelete: k => (typeof caches !== 'undefined') ? caches.delete(k) : Promise.resolve(false),
  }
}

/**
 * Purga SW + cachés y recarga. SIEMPRE disparado por un click del usuario —
 * nunca automático (G-245: no destruir trabajo sin guardar).
 *
 * Best-effort con tope: si el purgado no termina en `SW_RESET_TIMEOUT_MS`,
 * recargamos de todos modos.
 */
export async function hardResetAndReload (): Promise<void> {
  const purge = purgeServiceWorkerAndCaches(browserSwResetDeps())
  const timeout = new Promise<void>(resolve => setTimeout(resolve, SW_RESET_TIMEOUT_MS))
  await Promise.race([purge, timeout])
  window.location.reload()
}
